import React, { useCallback, useRef, useState } from 'react';
import './draggable-entity-card.css';
import { ENTITY_COLORS, getCanvasEntityDOMId } from '../../services/editor/ui';
import {
	ChangeSet,
	EntryPointEntityState,
	ProjectState,
	CanvasEntityDisabledReason,
	StateMutationAction,
	EntityType,
} from '@elyx-code/project-logic-tree';
import { EditorService } from '../../services/editor';
import './collapse-entity-button.css';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import { useCanvasPopup } from '../../services/editor/CanvasPopup';
import { CanvasEntityElement } from '../../services/editor/CanvasElement';
import { Canvas } from '../../lib/canvas';

interface ICollapseEntityButtonProps {
	canvasElement: CanvasEntityElement;
	entity: EntryPointEntityState;
	project: EditorService;
	parentDisabledReasons: CanvasEntityDisabledReason[];
	small?: boolean;
}

export default function CollapseEntityButton(
	props: ICollapseEntityButtonProps,
) {
	const rootElementRef = useRef(null);

	const useForceUpdate = () => {
		const [value, setValue] = useState(0);
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	const memoizedCanvasPopupBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				containerClasses={['tooltip', 'tooltip--large-font']}
			>
				{props.entity.collapsed ? 'Expand hidden logic' : 'Hide logic branch'}
			</CanvasPopupBaseComponent>
		),
		[props.entity.collapsed],
	);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id: 'canvas--collapse-entity-button--' + props.entity.id,
			arrowClasses: ['tooltip-arrow'],
			anchorQuerySelector:
				'#' + getCanvasEntityDOMId(props.entity) + '--collapse-expand-node',
			placement: 'top',
			parentCanvasObject: props.canvasElement.draggableObject,
		},
		memoizedCanvasPopupBody,
		// [props.entity.collapsed]
	)
		.setupHover({
			closeDelay: 80,
			openDelay: 600,
		})
		.onBeforeShow(() => {
			if (props.parentDisabledReasons.length) {
				return false;
			}

			return true;
		});

	const onClick = (e: any) => {
		e.stopPropagation();

		let actionName = props.entity.collapsed ? StateMutationAction.ShowTriggerBody : StateMutationAction.HideTriggerBody;

		if (entity.parent?.type === EntityType.Loop) {
			actionName = props.entity.collapsed ? StateMutationAction.ShowLoopBody : StateMutationAction.HideLoopBody;
		} else if (entity.type === EntityType.FunctionDeclaration) {
			actionName = props.entity.collapsed ? StateMutationAction.ShowFuncDeclBody : StateMutationAction.HideFuncDeclBody;
		} else if (entity.type === EntityType.GlobalEvent) {
			actionName = props.entity.collapsed ? StateMutationAction.ShowGlobalEventBody : StateMutationAction.HideGlobalEventBody;
		}

		const changeSet = props.project.logic.addChangeSet(
			new ChangeSet(
				props.project.logic,
				ProjectState.sessionAuthor,
				new Date().toISOString(),
				props.entity,
				false,
				actionName,
			),
		);

		const previousCollapsedState = props.entity.collapsed;

		props.entity.metaSync(
			{
				collapsed: !previousCollapsedState,
			},
			changeSet,
		);

		if (previousCollapsedState) {
			props.project.renderFromExisting(props.entity, changeSet, () => {
				forceUpdate();

				props.project.renderAndCloseChangeSet(changeSet);
			});
		} else {
			props.project.removeFromExisting(props.entity, () => {
				forceUpdate();

				props.project.renderAndCloseChangeSet(changeSet);
			});
		}
	};

	const entity = props.entity;
	const entityColor = ENTITY_COLORS[entity.type];
	const small = props.small || false;

	return (
		<span ref={rootElementRef}>
			<button
				ref={canvasPopup.anchorRef}
				disabled={!!props.parentDisabledReasons.length}
				className={[
					'collapse-entity-button',
					small
						? 'collapse-entity-button__small'
						: 'collapse-entity-button__regular',
					// Add 'disabled' class if it is disabled
					...(!!props.parentDisabledReasons.length ? ['disabled'] : []),
				].join(' ')}
				id={`${getCanvasEntityDOMId(props.entity)}--collapse-expand-node`}
				{...{
					[Canvas.OBJ_ATTR_NAME]: `${getCanvasEntityDOMId(
						props.entity,
					)}--collapse-expand-node`,
				}}
				style={{
					background: `linear-gradient(to bottom, ${entityColor} 0%, ${entityColor} 50%, ${entityColor}00 100%)`,
				}}
				onClick={onClick}
				onMouseDown={(e) => {
					e.stopPropagation();
				}}
			>
				<span
					style={{
						display: 'flex',
						gap: '6px',
						alignItems: 'center',
						position: 'relative',
					}}
				>
					<i
						className={`fa-solid ${
							props.entity.collapsed ? 'fa-expand' : 'fa-compress'
						}`}
					/>
				</span>
			</button>
		</span>
	);
}
